import { Target, Skull, Swords, Shield, Crosshair, type LucideIcon } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Mode configuration — frontend mirror of src/shared/modes.ts
// ─────────────────────────────────────────────────────────────────────────────

export interface ModeConfig {
  key: string;
  label: string;
  shortLabel: string;
  type: 'round' | 'score';
  winScore: number;
  maxScore?: number;
  seedingPointsPerUnit?: number;
  seedingUnitLabel?: string;
}

export interface SeriesFormat {
  length: number;
  modeOrder: string[];
  winsNeeded: number;
}

export interface Preset {
  id: string;
  label: string;
  modes: ModeConfig[];
  series: SeriesFormat;
}

// ── CDL 2026 — Black Ops 7 ──────────────────────────────────────────────────

export const CDL_2026_MODES: ModeConfig[] = [
  { key: 'hardpoint', label: 'Hardpoint', shortLabel: 'HP', type: 'score', winScore: 250, seedingPointsPerUnit: 1, seedingUnitLabel: 'point' },
  { key: 'snd', label: 'Search & Destroy', shortLabel: 'S&D', type: 'round', winScore: 6, maxScore: 11, seedingPointsPerUnit: 50, seedingUnitLabel: 'round' },
  { key: 'overload', label: 'Overload', shortLabel: 'OVR', type: 'score', winScore: 8, seedingPointsPerUnit: 50, seedingUnitLabel: 'point' },
];

export const CDL_2026_BO5: SeriesFormat = { length: 5, modeOrder: ['hardpoint', 'snd', 'overload', 'hardpoint', 'snd'], winsNeeded: 3 };
export const CDL_2026_BO3: SeriesFormat = { length: 3, modeOrder: ['hardpoint', 'snd', 'overload'], winsNeeded: 2 };
export const CDL_2026_BO7: SeriesFormat = { length: 7, modeOrder: ['hardpoint', 'snd', 'overload', 'hardpoint', 'snd', 'overload', 'hardpoint'], winsNeeded: 4 };

// ── CDL 2025 — Black Ops 6 ──────────────────────────────────────────────────

export const CDL_2025_MODES: ModeConfig[] = [
  { key: 'hardpoint', label: 'Hardpoint', shortLabel: 'HP', type: 'score', winScore: 250, seedingPointsPerUnit: 1, seedingUnitLabel: 'point' },
  { key: 'snd', label: 'Search & Destroy', shortLabel: 'S&D', type: 'round', winScore: 6, maxScore: 11, seedingPointsPerUnit: 50, seedingUnitLabel: 'round' },
  { key: 'control', label: 'Control', shortLabel: 'CTL', type: 'round', winScore: 3, maxScore: 5, seedingPointsPerUnit: 50, seedingUnitLabel: 'round' },
];

export const CDL_2025_BO5: SeriesFormat = { length: 5, modeOrder: ['hardpoint', 'snd', 'control', 'hardpoint', 'snd'], winsNeeded: 3 };

// ── Presets ──────────────────────────────────────────────────────────────────

// P2-b (guided-wizard Phase 2, Lisa 2026-07-16): default series format is
// now Bo5 (was Bo3) — CompetitionCreateForm.tsx's create-mode initial state
// reads PRESETS[0] directly (presetId/modes/seriesFormat), so the default
// preset is the one moved to index 0, not just relabeled. Still fully
// overridable — every preset below remains selectable, this only changes
// which one is pre-selected.
export const PRESETS: Preset[] = [
  { id: 'cdl_2026_bo5', label: 'CDL 2026 (Bo5) — default', modes: CDL_2026_MODES, series: CDL_2026_BO5 },
  { id: 'cdl_2026_bo3', label: 'CDL 2026 (Bo3)', modes: CDL_2026_MODES, series: CDL_2026_BO3 },
  { id: 'cdl_2026_bo7', label: 'CDL 2026 (Bo7)', modes: CDL_2026_MODES, series: CDL_2026_BO7 },
  { id: 'cdl_2025_bo5', label: 'CDL 2025 (Bo5)', modes: CDL_2025_MODES, series: CDL_2025_BO5 },
];

// Series cost multipliers — Bo3 is the baseline (1.0x). Per Luke 2026-05-03:
// "BO 7 is over double the cost of BO 3."
// TODO(luke): confirm exact pricing model — these are reasonable defaults for surfacing
// relative cost in the UI; they don't affect billing logic anywhere yet.
export const SERIES_COST_MULTIPLIER: Record<number, number> = {
  1: 0.5,
  3: 1.0,
  5: 1.6,
  7: 2.2,
  9: 2.8,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const MODE_ICONS: Record<string, LucideIcon> = {
  snd: Crosshair,
  hardpoint: Skull,
  overload: Swords,
  control: Shield,
};

const DEFAULT_ICON = Target;

export function getModeByKey(modes: ModeConfig[], key: string): ModeConfig | undefined {
  return modes.find(m => m.key === key);
}

export function getModeLabel(modes: ModeConfig[], key: string): string {
  return getModeByKey(modes, key)?.label ?? key;
}

export function getModeShortLabel(modes: ModeConfig[], key: string): string {
  return getModeByKey(modes, key)?.shortLabel ?? key.toUpperCase();
}

export function getModeIcon(key: string): LucideIcon {
  return MODE_ICONS[key] ?? DEFAULT_ICON;
}

export function validateSeriesFormat(modes: ModeConfig[], series: SeriesFormat): string | null {
  if (series.length < 1 || series.length > 9) return 'Series length must be 1-9';
  if (series.length % 2 === 0) return 'Series length must be odd';
  if (series.modeOrder.length !== series.length) return `Mode order length (${series.modeOrder.length}) must match series length (${series.length})`;
  if (series.winsNeeded !== Math.ceil(series.length / 2)) return `Wins needed must be ${Math.ceil(series.length / 2)} for a Bo${series.length}`;
  const modeKeys = new Set(modes.map(m => m.key));
  for (const key of series.modeOrder) {
    if (!modeKeys.has(key)) return `Mode "${key}" in series order is not in the modes list`;
  }
  return null;
}
